import { apiRequest } from './apiClient';

const HISTORY_SCOPE_KEYS = ['action_id', 'datasource_id', 'timeevent_id', 'action_type', 'status', 'execution_context',
  'widget_variable', 'start_date', 'end_date'];

export const getActionHistoryScope = (options = {}) => Object.fromEntries(HISTORY_SCOPE_KEYS
  .filter(key => options[key] !== undefined && options[key] !== null && String(options[key]).trim() !== '')
  .map(key => [key, options[key]]));

const appendOptionalParam = (params, key, value) => {
  if (value === undefined || value === null || value === '') {
    return;
  }
  params.set(key, String(value));
};

export const fetchActionLogs = async (options = {}) => {
  const params = new URLSearchParams();
  appendOptionalParam(params, 'requestId', options.requestId);
  appendOptionalParam(params, 'caseid', options.caseid);
  appendOptionalParam(params, 'action_type', options.action_type);
  appendOptionalParam(params, 'status', options.status);
  appendOptionalParam(params, 'execution_context', options.execution_context);
  appendOptionalParam(params, 'limit', options.limit);
  appendOptionalParam(params, 'start_date', options.start_date);
  appendOptionalParam(params, 'end_date', options.end_date);
  appendOptionalParam(params, 'timeevent_id', options.timeevent_id);
  appendOptionalParam(params, 'widget_variable', options.widget_variable);
  appendOptionalParam(params, 'action_id', options.action_id);
  appendOptionalParam(params, 'datasource_id', options.datasource_id);
  appendOptionalParam(params, 'pagination_mode', options.pagination_mode || 'offset');
  appendOptionalParam(params, 'cursor', options.cursor);
  appendOptionalParam(params, 'page', options.page);

  const query = params.toString();
  return apiRequest(`/api/v1/action-logs${query ? `?${query}` : ''}`);
};

export const fetchActionLogsStats = async (options = {}) => {
  const params = new URLSearchParams();
  appendOptionalParam(params, 'requestId', options.requestId);
  appendOptionalParam(params, 'caseid', options.caseid);
  appendOptionalParam(params, 'start_date', options.start_date);
  appendOptionalParam(params, 'end_date', options.end_date);

  const query = params.toString();
  return apiRequest(`/api/v1/action-logs/stats${query ? `?${query}` : ''}`);
};

export const fetchActionBrowserEvidence = (caseId, logId, index = 0, signal) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/action-logs/${encodeURIComponent(logId)}/browser-evidence?index=${index}`,
  { signal }
);

export const askActivityAssistant = (caseId, payload, signal) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/activity-assistant`,
  { method: 'POST', body: JSON.stringify(payload), signal }
);

export const fetchProjectWorkflowFlows = async (caseId) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows`);
};

export const runProjectActionLogicalGroup = (caseId, groupId, timeeventIds = null) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/actions/logical-groups/${encodeURIComponent(groupId)}/run`,
  {
    method: 'POST',
    ...(Array.isArray(timeeventIds) ? { body: JSON.stringify({ timeevent_ids: timeeventIds }) } : {}),
  }
);

export const createProjectWorkflowFlow = async (caseId, payload = {}) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const updateProjectWorkflowFlowAutomation = async (caseId, flowId, payload = {}) => {
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows/${encodeURIComponent(flowId)}/automation`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }
  );
};

export const deleteProjectWorkflowFlow = async (caseId, flowId, payload = {}) => {
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows/${encodeURIComponent(flowId)}`,
    {
      method: 'DELETE',
      body: JSON.stringify(payload)
    }
  );
};

export const updateProjectWorkflowFlowLayout = async (caseId, flowId, nodePositions = {}) => {
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/workflow-flows/${encodeURIComponent(flowId)}/layout`,
    {
      method: 'PATCH',
      body: JSON.stringify({ node_positions: nodePositions })
    }
  );
};

export const fetchProjectDagLayout = async (caseId) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/dag-layout`);
};

export const fetchProjectDagRevisions = (caseId, before = null) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/dag-revisions${before ? `?before=${encodeURIComponent(before)}` : ''}`
);

export const fetchProjectDagRevisionChanges = (caseId, revisionId, after = 0) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/dag-revisions/${encodeURIComponent(revisionId)}/changes?after=${encodeURIComponent(after)}`
);

export const restoreProjectDagRevision = (caseId, revisionId, headChangeId, detachTemplate = false) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/dag-revisions/${encodeURIComponent(revisionId)}/restore`,
  { method: 'POST', body: JSON.stringify({ expected_head_change_id: headChangeId, detach_template: detachTemplate === true }) }
);

export const explainProjectDag = (caseId, payload, signal) => apiRequest(
  `/api/v1/projects/${encodeURIComponent(caseId)}/dag-explanation`,
  { method: 'POST', body: JSON.stringify(payload), signal }
);

export const updateProjectDagLayout = async (
  caseId,
  nodePositions = {},
  handlePositions = {}
) => {
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/dag-layout`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        node_positions: nodePositions,
        handle_positions: handlePositions,
      })
    }
  );
};

export const fetchProjectTemplateSetup = async (caseId) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/template-setup`);
};

export const fetchProjectTemplateSetupStatus = async (caseId) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/template-setup?status_only=true`);
};

export const updateProjectTemplateSetupCompletion = async (
  caseId,
  requirementId,
  completed
) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/template-setup`, {
    method: 'PATCH',
    body: JSON.stringify({ requirement_id: requirementId, completed: Boolean(completed) })
  });
};

export const fetchProjectAiTokenUsage = async (caseId, options = {}) => {
  const params = new URLSearchParams();
  appendOptionalParam(params, 'group_by', options.groupBy || options.group_by || 'day');
  appendOptionalParam(params, 'start_date', options.start_date);
  appendOptionalParam(params, 'end_date', options.end_date);
  const query = params.toString();
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/ai-token-usage${query ? `?${query}` : ''}`);
};
